import { useState, useEffect, useRef } from "react";
import { Send, CheckCircle, XCircle, AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";
const DAILY_LIMIT = 200;

export default function BulkIndexingCard({ isGscConnected }) {
  const [candidates, setCandidates] = useState([]);
  const [allUrls, setAllUrls] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({ status: "idle" });
  const [showAll, setShowAll] = useState(false);
  const [showList, setShowList] = useState(false);
  const pollRef = useRef(null);

  const loadData = async () => {
    if (!isGscConnected) return;
    setLoading(true);
    try {
      const [{ candidates: c, all }, q] = await Promise.all([
        api.getIndexingCandidates(),
        api.getIndexingQuota(),
      ]);
      setCandidates(c || []);
      setAllUrls(all || []);
      setQuota(q);
      // Pre-select not-indexed candidates, capped at remaining daily quota
      const cap = q?.remaining ?? DAILY_LIMIT;
      setSelected(new Set((c || []).slice(0, cap).map(x => x.url)));
    } catch (e) {
      console.error("[BulkIndexing] loadData error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [isGscConnected]);

  const pollStatus = () => {
    let consecutiveErrors = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.getIndexingStatus();
        consecutiveErrors = 0;
        setState(s);
        if (s.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          api.getIndexingQuota().then(setQuota).catch(() => {});
        }
      } catch (e) {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setState({ status: "failed", message: "Lost connection to server. Reload to check status." });
        }
      }
    }, 1500);
  };

  const handleSubmit = async () => {
    const urls = Array.from(selected);
    if (!urls.length) return;
    const wpPostIdByUrl = new Map([...candidates, ...allUrls].map(x => [x.url, x.wp_post_id]));
    const items = urls.map(url => ({ url, wp_post_id: wpPostIdByUrl.get(url) ?? null }));
    setState({ status: "running", submitted: 0, total: items.length, errors: 0, message: "Starting..." });
    try {
      const res = await api.submitIndexing(items);
      pollStatus();
    } catch (err) {
      console.error("[BulkIndexing] submit error:", err?.response?.data || err);
      setState({
        status: "failed",
        message: err.response?.data?.detail || "Submit failed.",
      });
    }
  };

  const handleStop = async () => {
    try {
      await api.stopIndexing();
    } catch (e) {
      console.error("[BulkIndexing] stop error:", e?.response?.data || e);
    }
  };

  const toggleUrl = (url) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const toggleSelectAll = (list) => {
    const allSelected = list.every(x => selected.has(x.url));
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        list.forEach(x => next.delete(x.url));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        list.forEach(x => next.add(x.url));
        return next;
      });
    }
  };

  const displayList = showAll ? allUrls : candidates;
  const isRunning = state.status === "running";
  const remaining = quota?.remaining ?? DAILY_LIMIT;
  const canSubmit = selected.size > 0 && !isRunning && remaining > 0;
  const overQuota = selected.size > remaining;

  if (!isGscConnected) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100">
        <Send size={14} strokeWidth={2} className="text-zinc-900" />
        <div className="flex-1">
          <p className="text-sm font-bold text-zinc-900">Bulk Indexing</p>
          <p className="text-[10px] text-zinc-500">Google Indexing API — 200 URLs/day</p>
        </div>
        {quota && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            remaining === 0
              ? "bg-red-100 text-red-700"
              : remaining < 50
              ? "bg-orange-100 text-orange-700"
              : "bg-green-100 text-green-700"
          }`}>
            {remaining}/{DAILY_LIMIT} left today
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {loading && (
          <p className="text-xs text-zinc-400">Loading candidates...</p>
        )}

        {!loading && (
          <>
            {/* Scope warning — shown if we detect a 403 scope error */}
            {state.status === "failed" && state.message?.toLowerCase().includes("scope") && (
              <div className="flex items-start gap-2 px-4 py-3 text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded-lg">
                <AlertTriangle size={14} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Reconnect required.</strong> Your current Google session lacks the indexing permission.
                  Use "Reconnect" under <a href="/dashboard/settings/search-console" className="underline font-bold">Search Console</a> to re-authorize with indexing access.
                </span>
              </div>
            )}

            {/* URL source toggle */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setShowAll(false); setSelected(new Set(candidates.slice(0, remaining).map(x => x.url))); }}
                className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${
                  !showAll ? "text-white" : "bg-white border border-zinc-100 text-zinc-500 hover:bg-zinc-50"
                }`}
                style={!showAll ? { backgroundColor: PRIMARY } : {}}
              >
                Not indexed ({candidates.length})
              </button>
              <button
                type="button"
                onClick={() => { setShowAll(true); setSelected(new Set(allUrls.slice(0, remaining).map(x => x.url))); }}
                className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${
                  showAll ? "text-white" : "bg-white border border-zinc-100 text-zinc-500 hover:bg-zinc-50"
                }`}
                style={showAll ? { backgroundColor: PRIMARY } : {}}
              >
                All articles ({allUrls.length})
              </button>
              <button
                type="button"
                onClick={() => loadData()}
                className="ml-auto text-zinc-400 hover:text-zinc-900 transition-colors"
                title="Reload"
              >
                <RefreshCw size={13} strokeWidth={2} />
              </button>
            </div>

            {/* URL list (collapsible) */}
            {displayList.length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-4">
                {showAll ? "No articles with URLs found." : "All articles are indexed."}
              </p>
            )}

            {displayList.length > 0 && (
              <div className="border border-zinc-100 rounded-lg overflow-hidden">
                {/* Select all row */}
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-100">
                  <input
                    type="checkbox"
                    checked={displayList.length > 0 && displayList.every(x => selected.has(x.url))}
                    onChange={() => toggleSelectAll(displayList)}
                    className="w-3.5 h-3.5 accent-zinc-900"
                  />
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                    {selected.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowList(v => !v)}
                    className="ml-auto text-zinc-400 hover:text-zinc-900"
                  >
                    {showList ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                </div>

                {/* URL rows (collapsed by default — show summary) */}
                {showList && (
                  <div className="max-h-48 overflow-y-auto">
                    {displayList.map(item => (
                      <label
                        key={item.url}
                        className="flex items-start gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer border-b border-zinc-50 last:border-0"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(item.url)}
                          onChange={() => toggleUrl(item.url)}
                          className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 accent-zinc-900"
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-zinc-900 truncate">{item.title || item.url}</p>
                          <p className="text-[10px] text-zinc-400 truncate font-mono">{item.url}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quota warning */}
            {overQuota && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded-lg">
                <AlertTriangle size={13} strokeWidth={2} />
                {selected.size} selected but only {remaining} quota remaining. First {remaining} URLs will be submitted.
              </div>
            )}

            {/* Submit / Stop button */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex-1 px-4 py-3 text-xs font-bold text-white disabled:opacity-40 transition-colors rounded-lg hover:opacity-90 flex items-center justify-center gap-2"
                style={{ backgroundColor: PRIMARY }}
              >
                {isRunning ? (
                  <>
                    <RefreshCw size={13} strokeWidth={2} className="animate-spin" />
                    {state.message || "Submitting..."}
                  </>
                ) : (
                  <>
                    <Send size={13} strokeWidth={2} />
                    Request Indexing ({Math.min(selected.size, remaining)} URLs)
                  </>
                )}
              </button>
              {isRunning && (
                <button
                  type="button"
                  onClick={handleStop}
                  disabled={state.stop_requested}
                  className="px-4 py-3 text-xs font-bold text-red-600 bg-red-50 border border-red-200 disabled:opacity-40 transition-colors rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
                >
                  <XCircle size={13} strokeWidth={2} />
                  {state.stop_requested ? "Stopping..." : "Stop"}
                </button>
              )}
            </div>

            {/* Progress / result */}
            {isRunning && (
              <div className="bg-zinc-50 border border-zinc-100 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1.5">
                  <span>Progress</span>
                  <span className="font-bold text-zinc-900">{state.submitted}/{state.total}</span>
                </div>
                <div className="w-full bg-zinc-200 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{ backgroundColor: PRIMARY, width: `${state.total ? (state.submitted / state.total) * 100 : 0}%` }}
                  />
                </div>
                {state.errors > 0 && (
                  <p className="text-[10px] text-red-500 mt-1">{state.errors} error(s)</p>
                )}
              </div>
            )}

            {(state.status === "completed" || state.status === "failed" || state.status === "stopped") && !isRunning && (
              <div className={`flex items-start gap-2 px-4 py-3 text-xs border rounded-lg ${
                state.status === "completed"
                  ? "bg-green-50 border-green-200 text-green-700"
                  : state.status === "stopped"
                  ? "bg-orange-50 border-orange-200 text-orange-700"
                  : "bg-red-50 border-red-200 text-red-600"
              }`}>
                {state.status === "completed"
                  ? <CheckCircle size={14} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                  : <XCircle size={14} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                }
                <span>{state.message}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
